import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../shared/useFetchMoreCallback';
import { questionsQuery, questionsQueryVariables } from '../generated/questionsQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { questionFieldsFragment } from '../models/Question';
import { QuestionType } from '../models/codeOnly/QuestionType';

export interface QuestionsAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    questionTypes?: Array<QuestionType>,
}

/**
 * Get a list of questions from the store.
 * @param id
 * @param options
 */
export function useQuestions(options: QuestionsAsyncLoadPageOptions = {}): AsyncLoadPagedResult<questionsQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<questionsQuery, questionsQueryVariables>(
        gql`
        query questionsQuery ($offset: Int, $first: Int, $questionTypes: [String!]) {
            items: questions (offset: $offset, first: $first, questionTypes: $questionTypes) {
                ...questionFields
            }
        }

        ${questionFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                questionTypes: options.questionTypes,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<questionsQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])]
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);
    
    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
